/**
 * "Afterglow" — original speed-showcase level (difficulty 5/10).
 * Escalating tempo: a 1× rhythm intro, a 2× band, a streak tunnel, a 3×
 * pad/orb sprint, an inverted ceiling run, and a full-speed finale — dressed
 * with camera and shader ambience triggers throughout.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0, hanging = false): void =>
  push({ type: 'spike', ...cell(bx, by), ...(hanging ? { flipY: true } : {}) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};
const column = (bx: number, fromY: number, toY: number): void => {
  for (let by = fromY; by <= toY; by++) block(bx, by);
};
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const pad = (bx: number, kind: string): void =>
  push({ type: 'pad', ...cell(bx, 0), props: { kind } });
const orb = (bx: number, by: number, kind: string): void =>
  push({ type: 'orb', ...cell(bx, by), props: { kind } });
const trig = (bx: number, props: Record<string, number | string | boolean>): void =>
  push({ type: 'trigger', ...cell(bx, 1), props });

// --- Act 1: 1× rhythm intro (0–66) --------------------------------------------
trig(4, { kind: 'color', channel: 1, hex: '#f0abfc', duration: 1 });
spike(24);
spike(32);
spike(40);
spike(41);
spike(50);
spike(58);
spike(59);

// --- Act 2: 2× band (66–108) ----------------------------------------------------
portal(66, 'speed:two');
trig(66.5, { kind: 'shader:shockline', strength: 0.8, duration: 0.6 });
spike(76);
spike(86);
spike(96);
spike(97);
spike(104);

// --- Act 3: streak tunnel (108–152) ---------------------------------------------
portal(112, 'form:streak');
ceiling(114, 148, 6);
column(122, 0, 1); // stalagmite
column(132, 4, 5); // stalactite
column(142, 0, 1);
portal(152, 'form:runner', 1);
portal(152, 'form:runner', 4); // catch the streak at either altitude

// --- Act 4: 3× pad/orb sprint (156–200) ------------------------------------------
portal(156, 'speed:three');
trig(156.5, { kind: 'camera:zoom', zoom: 1.25, duration: 0.8, easing: 'inout' });
pad(168, 'strong');
spike(169);
spike(170);
spike(171);
trig(168.5, { kind: 'camera:shake', strength: 6, duration: 0.4 });
orb(184, 1, 'strong');
spike(184);
spike(185);
spike(186);
spike(198);

// --- Act 5: inverted ceiling run (204–234) ---------------------------------------
portal(206, 'gravity:up');
trig(206.5, { kind: 'shader:chromatic', strength: 0.5, duration: 0.6 });
ceiling(208, 232, 6);
spike(216, 5, true);
spike(224, 5, true);
portal(230, 'gravity:down', 5);
portal(230, 'gravity:down', 1); // safety catch if already grounded
trig(232, { kind: 'shader:chromatic', strength: 0, duration: 0.6 });
trig(233, { kind: 'camera:zoom', zoom: 1, duration: 0.8, easing: 'inout' });

// --- Finale: full-speed doubles (234–260) ----------------------------------------
spike(240);
spike(241);
spike(250);

// Secret shards on the risky lines.
push({ type: 'collectible', ...cell(45, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(133, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(187, 3), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(260, 2) });

export const AFTERGLOW: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Afterglow',
    author: 'Shape Shifters Team',
    description: 'The grid accelerates. Try to keep up.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 5,
    tags: ['showcase', 'speed', 'official'],
  },
  song: { id: 'voltage', bpm: 150, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#f0abfc',
    hazardColor: '#f43f5e',
    backgroundColor: '#1a0b2e',
  },
  objects,
};
